import type { EnumKind, EnumOption } from '@anytracker/core'

import styles from './EnumInput.module.css'
import type { ValueInputProps } from './types.js'
import { useRadioGroupKeys } from './useRadioGroupKeys.js'

interface Props extends ValueInputProps {
  readonly kind: EnumKind
}

/**
 * Ordered enums render in score order; unordered ones in declared order.
 *
 * The distinction is not cosmetic. An ordered enum is a scale — `none → mild →
 * strong` has to read left-to-right as increasing, or the control silently
 * misrepresents the data it collects. An unordered one has no such order, so
 * imposing one (alphabetical, say) would invent a ranking the author did not
 * mean.
 */
function displayOrder(kind: EnumKind): readonly EnumOption[] {
  if (!kind.ordered) {
    return kind.options
  }
  // Copy before sorting: `options` is readonly and shared with the stored track.
  return [...kind.options].sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
}

export function EnumInput({
  kind,
  value,
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
  variant = 'compact',
}: Props) {
  // A value is an option **id**, never a label — renaming an option must not
  // rewrite history (ADR-0005).
  const current = typeof value === 'string' ? value : null

  const ordered = displayOrder(kind)
  const selectedIndex = ordered.findIndex((option) => option.id === current)
  const keys = useRadioGroupKeys(ordered.length, selectedIndex, (index) => {
    const option = ordered[index]
    if (option) {
      onChange(option.id)
    }
  })

  return (
    <div
      className={`${styles['root']} ${variant === 'full' ? styles['full'] : ''}`}
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
    >
      {ordered.map((option, index) => {
        const selected = current === option.id
        return (
          <button
            key={option.id}
            ref={keys.register(index)}
            type="button"
            onKeyDown={keys.onKeyDown}
            role="radio"
            aria-checked={selected}
            tabIndex={keys.tabIndexFor(index)}
            disabled={disabled}
            className={`${styles['option']} ${selected ? styles['selected'] : ''}`}
            onClick={() => onChange(selected ? null : option.id)}
          >
            {option.colour !== undefined && (
              <span
                className={styles['swatch']}
                style={{ background: option.colour }}
                aria-hidden="true"
              />
            )}
            <span className={styles['label']}>{option.label}</span>
            {variant === 'full' && selected && (
              <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="none"
                aria-hidden="true"
                className={styles['tick']}
              >
                <path
                  d="M5 13l4 4 10-10"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )
      })}
    </div>
  )
}
